import { Sliders } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockRow, MockSelect, MockField, MockButton } from "../shared";

export default function AdminFieldPermissionsSection() {
  return (
    <div data-testid="guide-admin-field-permissions">
      <PageHeading
        icon={Sliders}
        title="Field Permissions"
        description="Rules that decide who can edit which boxes on the Register / Detail forms, and during which stage of an item's life."
      />

      <Callout variant="info" title="In plain words">
        This is where you decide, for each form field, who is allowed to change it and when. For example, you might let General Admins edit the due date while an item is in progress, but lock it once it reaches MD review. If you don't add a rule, the field is read-only by default.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="super_admin" /><span className="text-xs text-white/55"><span className="text-white">manage_governance</span> on a draft version.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it when a certain role needs to be able to edit (or be stopped from editing) a particular field at a particular stage. You always edit a draft version and activate it when ready.
        </p>
      </Section>

      <Section title="Adding or changing a rule">
        <Diagram
          frameLabel="Admin → Field Permissions (on a draft version)"
          legend={[
            <>Click <span className="text-white">Add Rule</span>.</>,
            <>Pick the <span className="text-white">field</span>, the <span className="text-white">role</span>, and the lifecycle <span className="text-white">phase</span>.</>,
            <>Turn <span className="text-white">Can edit</span> on (editable) or off (read-only).</>,
            <>Click <span className="text-white">Save</span>, then activate the draft from Versions.</>,
          ]}
        >
          <MockButton label="Add Rule" marker={1} />
          <MockRow>
            <MockSelect label="Field key" value="due_date" marker={2} />
            <MockSelect label="Role key" value="general_admin" />
            <MockSelect label="Phase key" value="in_progress" />
          </MockRow>
          <MockField label="Can edit" value="Yes" marker={3} />
          <MockButton label="Save" marker={4} />
        </Diagram>
        <Steps steps={[
          <>On a draft governance version, open the Field Permissions tab.</>,
          <>Click <span className="text-white">Add Rule</span>. Pick the field key, the role key, and the lifecycle phase.</>,
          <>Toggle <span className="text-white">Can edit</span> on or off.</>,
          <>Save the row. Repeat for every (field × role × phase) combination you want to override away from default-deny.</>,
          <>Test with a non-super-admin login if you can — it is the easiest way to confirm you have not locked anyone out of fields they need.</>,
          <>Activate the draft from the Versions tab when ready.</>,
        ]} />
      </Section>

      <Section title="Rule fields">
        <div className="space-y-2">
          <Field name="Field key" required="required"><p>Which form field is being gated (e.g. <span className="text-white">due_date</span>, <span className="text-white">priority</span>, <span className="text-white">assigned_to</span>).</p></Field>
          <Field name="Role key" required="required"><p>Which role this rule applies to.</p></Field>
          <Field name="Phase key" required="required"><p>Which lifecycle phase this rule applies in (e.g. <span className="text-white">registration</span>, <span className="text-white">in_progress</span>, <span className="text-white">md_review</span>).</p></Field>
          <Field name="Can edit" required="required"><p>If on, the role can change the field during the phase. If off, the field is read-only for them.</p></Field>
        </div>
      </Section>

      <Callout variant="info" title="Default-deny">
        Roles without a rule for a field+phase combination default to read-only. To grant editing, add an explicit row.
      </Callout>
      <Callout variant="warning" title="Don't lock yourself out">
        It is possible to grant <span className="text-white">manage_governance</span> to a role and then field-permission them out of editing the very fields they need. Test the change with a non-super-admin user before activating the draft.
      </Callout>
    </div>
  );
}
